import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  QuestionMarkCircleIcon,
  BellIcon,
  UserIcon,
  HomeIcon,
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  LockClosedIcon,
  LockOpenIcon,
  Square3Stack3DIcon,
  EyeIcon,
  EyeSlashIcon,
  RectangleStackIcon,
  CircleStackIcon,
  ArrowRightIcon,
  MinusIcon,
  SwatchIcon,
  DocumentIcon,
  FaceSmileIcon,
  PaintBrushIcon,
  Squares2X2Icon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { fetchTemplateById, deleteTemplate, MoodBoardTemplate, TemplateElement, fetchTemplates } from '../utils/templateService';
import { fetchDocumentById, updateDocument, forkTemplateToDocument, DesignDocument, listenToElements, setElementsArray } from '../utils/documentService';
// Remove Firestore import, keep Realtime Database
// import { createDesign, updateDesign, FirestoreDesign, fetchUserDesigns } from '../utils/firestoreDesignService';
import { AuthContext } from '../contexts/AuthContext';
import KonvaCanvas from '../components/KonvaCanvas';

import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import UpgradeToProModal from '../components/UpgradeToProModal';
import { subscribeUserPlan, isPro } from '../utils/planGate';

const EditorPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const authContext = React.useContext(AuthContext);
  const currentUser = authContext?.user;
  const [isDocument, setIsDocument] = useState(false);
  const [documentMeta, setDocumentMeta] = useState<DesignDocument | null>(null);
  const [isDirty, setIsDirtyState] = useState(false);
  const [template, setTemplate] = useState<MoodBoardTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [elements, setElements] = useState<TemplateElement[]>([]);
  const debounceRef = useRef<any>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canView, setCanView] = useState(true);
  const [revertState, setRevertState] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [stageRef, setStageRef] = useState<any>(null);
  const isDirtyRef = useRef<boolean>(false);
  
  // Unsaved changes hook
  const { handleNavigationRequest, UnsavedChangesModal } = useUnsavedChanges(
    isDirty,
    async () => {
      await handleSaveOnly(); // Use a version that doesn't navigate
    },
    async () => {
      await handleDiscardChanges(); // Restore original state
    }
  );
  
  // Mobile responsive states
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);
  
  // Canvas dimensions
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 1200, height: 800 });

  // New state variables
  const [editName, setEditName] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState('');
  const [hideGridForExport, setHideGridForExport] = useState(false);

  // Unsplash search states
  const [unsplashOpen, setUnsplashOpen] = useState(false);
  const [unsplashQuery, setUnsplashQuery] = useState('');
  const [unsplashResults, setUnsplashResults] = useState<any[]>([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);

  // New tool states
  const [activeShapeType, setActiveShapeType] = useState<'rectangle' | 'circle' | 'arrow' | 'line' | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<{ x: number; y: number }[]>([]);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(20);
  const [selectedColors, setSelectedColors] = useState<string[]>(['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7']);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState('#FF6B6B');
  const [showStickyNoteColors, setShowStickyNoteColors] = useState(false);
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [backgroundPattern, setBackgroundPattern] = useState<'none' | 'dots' | 'grid' | 'lines'>('none');
  const [showGuide, setShowGuide] = useState(false);

  // Plan gating state
  const [userPlan, setUserPlan] = useState<'free' | 'pro' | 'enterprise'>('free');
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = subscribeUserPlan(currentUser.uid, (plan) => setUserPlan(plan));
    return () => { if (unsub) unsub(); };
  }, [currentUser?.uid]);

  // Text edit modal states
  const [showTextEditModal, setShowTextEditModal] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [editingElementId, setEditingElementId] = useState<string | null>(null);

  // Detect blank new design mode
  const isNewBlank = templateId === 'new';

  // Template loading state
  const [availableTemplates, setAvailableTemplates] = useState<MoodBoardTemplate[]>([]);

  // Unsplash API key
  const UNSPLASH_ACCESS_KEY = '2lRBV8sO9SynWMDFMAxuiLE0MiHt6x8hzTpaUc4SveU';

  // Fetch template data from Firebase
  useEffect(() => {
    if (!templateId) return;
    if (templateId === 'new') {
      // Blank state
      console.log('Creating new blank design');
      setTemplate({
        id: 'new',
        name: 'Untitled Design',
        preview_image_url: '',
        elements: [],
        created_by: currentUser?.displayName || 'Unknown',
        timestamp: Date.now(),
      });
      setEditName('Untitled Design');
      setEditImageUrl('');
      setElements([]);
      setRevertState({ name: 'Untitled Design', preview_image_url: '', elements: [] });
      setIsDocument(false);
      setLoading(false);
      setError(null);
      console.log('Blank design initialized with editName:', 'Untitled Design');
      return;
    }
    
    const load = async () => {
      if (!currentUser?.uid) return;
      
      console.log('Loading editor with templateId:', templateId, 'isNewBlank:', isNewBlank);
      
      try {
        setLoading(true);
        setError(null);

        // For new blank designs
        if (templateId === 'new') {
          // Blank state
          console.log('Creating new blank design');
          setTemplate({
            id: 'new',
            name: 'Untitled Design',
            preview_image_url: '',
            elements: [],
            created_by: currentUser?.displayName || 'Unknown',
            timestamp: Date.now(),
          });
          setEditName('Untitled Design');
          setEditImageUrl('');
          setElements([]);
          setRevertState({ name: 'Untitled Design', preview_image_url: '', elements: [] });
          setIsDocument(false);
          setLoading(false);
          setError(null);
          console.log('Blank design initialized with editName:', 'Untitled Design');
          return;
        }

        // Try default template first
        const tplData = await fetchTemplateById(templateId);
        if (tplData) {
          const valid = (tplData.elements || []).filter(Boolean);
          setTemplate({ ...tplData, elements: valid });
          setEditName(tplData.name);
          setEditImageUrl(tplData.preview_image_url);
          setElements(valid);
          setRevertState({ name: tplData.name, preview_image_url: tplData.preview_image_url, elements: [...valid] });
          setIsDocument(false);
          setLoading(false);
          return;
        }

        // Check Firestore for user designs
        if (currentUser?.uid) {
          // This part of the logic is now handled by the documentService.fetchDocumentById
          // and the forkTemplateToDocument function.
          // If the templateId is a user document, we need to fetch it.
          console.log('Checking for user document with ID:', templateId);
          const docData = await fetchDocumentById(templateId);
          if (docData) {
            console.log('Found user document:', docData);
            const valid = (docData.elements || []).filter(Boolean);
            setTemplate({
              id: docData.id,
              name: docData.name,
              preview_image_url: docData.preview_image_url,
              elements: valid,
              created_by: currentUser.displayName || 'Unknown',
              timestamp: docData.last_modified,
            });
            setDocumentMeta(docData);
            setEditName(docData.name);
            setEditImageUrl(docData.preview_image_url);
            setElements(valid);
            setRevertState({ name: docData.name, preview_image_url: docData.preview_image_url, elements: [...valid] });
            setIsDocument(true);
            setLoading(false);
            console.log('User document loaded successfully');
            return;
          } else {
            console.log('No user document found with ID:', templateId);
          }
        }

        // Fallback to user document (Realtime Database) - this is redundant now
        console.log('Trying fallback document fetch for ID:', templateId);
        const docData = await fetchDocumentById(templateId);
        if (docData) {
          console.log('Found fallback document:', docData);
          const valid = (docData.elements || []).filter(Boolean);
          setTemplate({
            id: docData.id,
            name: docData.name,
            preview_image_url: docData.preview_image_url,
            elements: valid,
            created_by: currentUser?.displayName || 'Unknown',
            timestamp: docData.last_modified,
          });
          setDocumentMeta(docData);
          setEditName(docData.name);
          setEditImageUrl(docData.preview_image_url);
          setElements(valid);
          setRevertState({ name: docData.name, preview_image_url: docData.preview_image_url, elements: [...valid] });
          setIsDocument(true);
          setLoading(false);
          console.log('Fallback document loaded successfully');
          return;
        } else {
          console.log('No fallback document found with ID:', templateId);
        }

        setError('Document or template not found');
        setLoading(false);
      } catch (err) {
        console.error('Error loading resource:', err);
        setError('Error loading data');
        setLoading(false);
      }
    };

    load();
  }, [templateId, currentUser?.displayName]);

  // Handle unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Do you want to save your changes?';
        return 'You have unsaved changes. Do you want to save your changes?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);



  // This function is now replaced by the useUnsavedChanges hook

  // Load available templates for sidebar
  useEffect(() => {
    const unsubscribe = fetchTemplates((templates) => {
      setAvailableTemplates(templates);
    });
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  // Handle template selection
  const handleTemplateSelect = async (template: MoodBoardTemplate) => {
    if (!currentUser?.uid) return;
    
    try {
      console.log('Template selected:', template.name);
      
      // Show confirmation if user has unsaved changes
      if (isDirty) {
        const confirmed = window.confirm(
          'You have unsaved changes. Loading a new template will discard them. Continue?'
        );
        if (!confirmed) return;
      }
      
      // Load template content into current editor session without creating a document
      setTemplate(template);
      setEditName(`${template.name} (Copy)`); // Add "Copy" to indicate it's editable
      setEditImageUrl(template.preview_image_url);
      setElements(template.elements || []);
      setRevertState({ 
        name: `${template.name} (Copy)`, 
        preview_image_url: template.preview_image_url, 
        elements: [...(template.elements || [])] 
      });
      setIsDocument(false); // Keep as template mode until user saves
      setIsDirty(true); // Mark as dirty to encourage saving with new name
      
      console.log('Template loaded, editName set to:', `${template.name} (Copy)`);
      
      // Show success feedback
      const notification = document.createElement('div');
      notification.className = 'fixed top-20 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300';
      notification.textContent = `Template "${template.name}" loaded successfully!`;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);
      
    } catch (error) {
      console.error('Error loading template:', error);
      alert('Failed to load template. Please try again.');
    }
  };

  // Handle canvas sizing and scaling
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Calculate available canvas space
      let availableWidth, availableHeight;
      
      if (width < 1024) {
        // Mobile/tablet layout
        const headerHeight = 80; // mobile header
        const padding = 16; // mobile padding
        
        availableWidth = width - padding;
        availableHeight = height - headerHeight - padding;
      } else {
        // Desktop layout
        const sidebarWidth = 224; // sidebar width
        const headerHeight = 120; // desktop header + title bar height
        const padding = 64; // desktop padding
        
        availableWidth = width - sidebarWidth - padding;
        availableHeight = height - headerHeight - padding;
      }
      
      setCanvasDimensions({
        width: Math.max(800, Math.min(1400, availableWidth)),
        height: Math.max(600, Math.min(1000, availableHeight))
      });
      
      // Adjust scale for mobile
      if (width < 640) {
        setCanvasScale(0.5);
      } else if (width < 1024) {
        setCanvasScale(0.7);
      } else {
        setCanvasScale(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for real-time element changes
  useEffect(() => {
    if (!isDocument || !templateId) return;
    const unsub = listenToElements(templateId, (remoteElements) => {
      setElements((prev) => {
        // simple diff check
        if (JSON.stringify(prev) !== JSON.stringify(remoteElements)) {
          return remoteElements;
        }
        return prev;
      });
    });
    return () => { if (unsub) unsub(); };
  }, [isDocument, templateId]);

  const handleDownload = async () => {
    if (!isPro(userPlan)) {
      setShowUpgrade(true);
      return;
    }
    if (!template) return;
    // Hide grid for export
    setHideGridForExport(true);
    // Wait for the grid to hide
    await new Promise((resolve) => setTimeout(resolve, 50));
    if (stageRef) {
      stageRef.draw();
      const dataURL = stageRef.toDataURL({
        pixelRatio: 2,
        mimeType: 'image/png',
        quality: 1,
      });
      const link = document.createElement('a');
      link.href = dataURL;
      link.download = `${template.name}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    // Restore grid
    setHideGridForExport(false);
  };

  const handleUpdateThumbnail = async () => {
    if (!stageRef || elements.length === 0) {
      alert('No content to capture for thumbnail');
      return;
    }

    try {
      console.log('Manually updating thumbnail...');
      setHideGridForExport(true);
      await new Promise(resolve => setTimeout(resolve, 150));
      
      stageRef.draw();
      const dataURL = stageRef.toDataURL({
        pixelRatio: 1,
        mimeType: 'image/png',
        quality: 0.9,
        width: 400,
        height: 300,
      });

      if (dataURL && dataURL.length > 100 && dataURL.startsWith('data:image/')) {
        setEditImageUrl(dataURL);
        console.log('Thumbnail updated successfully');
        alert('Thumbnail updated! Save your design to keep the new thumbnail.');
        setIsDirty(true);
      } else {
        alert('Failed to capture thumbnail');
      }
      
      setHideGridForExport(false);
    } catch (error) {
      console.error('Failed to update thumbnail:', error);
      setHideGridForExport(false);
      alert('Failed to update thumbnail');
    }
  };

  const handleShare = () => setShowShareModal(true);
  const handleCloseShare = () => setShowShareModal(false);

  const handleRevert = () => {
    if (revertState) {
      setEditName(revertState.name);
      setEditImageUrl(revertState.preview_image_url);
      setElements((revertState.elements || []).filter(Boolean));
    }
  };

  const handleAddImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) {
          const newElement: TemplateElement = {
            id: `img-${Date.now()}`,
            type: 'image',
            src: ev.target!.result as string,
            x: 50 + Math.random() * (canvasDimensions.width - 250),
            y: 50 + Math.random() * (canvasDimensions.height - 250),
            width: 200,
            height: 200,
            layer: elements.length
          };
          setElements(prev => {
            setIsDirty(true);
            return [...prev, newElement];
          });
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  // Unsplash search functions
  const handleUnsplashSearch = async () => {
    setUnsplashLoading(true);
    setUnsplashResults([]);
    try {
      const res = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(unsplashQuery)}&per_page=12&client_id=${UNSPLASH_ACCESS_KEY}`);
      const data = await res.json();
      setUnsplashResults(data.results || []);
    } catch (err) {
      setUnsplashResults([]);
    }
    setUnsplashLoading(false);
  };

  const handleAddUnsplashImage = (url: string) => {
    const newElement: TemplateElement = {
      id: `img-${Date.now()}`,
      type: 'image',
      src: url,
      x: 50 + Math.random() * (canvasDimensions.width - 350),
      y: 50 + Math.random() * (canvasDimensions.height - 250),
      width: 300,
      height: 200,
      layer: elements.length
    };
    setElements(prev => {
      setIsDirty(true);
      return [...prev, newElement];
    });
    setUnsplashOpen(false);
    setUnsplashQuery('');
    setUnsplashResults([]);
  };

  const handleAddText = () => {
    setEditingText('New Text');
    setEditingElementId('new');
    setShowTextEditModal(true);
  };

  const handleTextEditSubmit = () => {
    if (editingElementId === 'new') {
      // Adding new text element
      const newElement: TemplateElement = {
        id: `txt-${Date.now()}`,
        type: 'text',
        text: editingText,
        x: 50 + Math.random() * (canvasDimensions.width - 200),
        y: 50 + Math.random() * (canvasDimensions.height - 100),
        color: '#000000',
        fontSize: 24,
        fontFamily: 'Arial',
        layer: elements.length
      };
      setElements(prev => {
        setIsDirty(true);
        return [...prev, newElement];
      });
    } else if (editingElementId) {
      // Editing existing text element
      setElements(prev => {
        setIsDirty(true);
        return prev.map(el => el.id === editingElementId ? { ...el, text: editingText } : el);
      });
    }
    setShowTextEditModal(false);
    setEditingText('');
    setEditingElementId(null);
  };

  const handleTextEditCancel = () => {
    setShowTextEditModal(false);
    setEditingText('');
    setEditingElementId(null);
  };

  const handleEditTextElement = (elementId: string, currentText: string) => {
    setEditingText(currentText);
    setEditingElementId(elementId);
    setShowTextEditModal(true);
  };

  const handleAddTextOld = () => {
    const newElement: TemplateElement = {
      id: `txt-${Date.now()}`,
      type: 'text',
      text: 'New Text',
      x: 50 + Math.random() * (canvasDimensions.width - 200),
      y: 50 + Math.random() * (canvasDimensions.height - 100),
      color: '#000000',
      fontSize: 24,
      fontFamily: 'Arial',
      layer: elements.length
    };
    setElements(prev => {
      setIsDirty(true);
      return [...prev, newElement];
    });
  };

  const handleDeleteElement = (elementId: string) => {
    setElements(prev => {
      setIsDirty(true);
      return prev.filter(el => el.id !== elementId);
    });
    setSelectedElementId(null);
  };

  const handleDuplicateElement = (elementId: string) => {
    const element = elements.find(el => el.id === elementId);
    if (element) {
      const newElement = {
        ...element,
        id: `${element.type}-${Date.now()}`,
        x: element.x + 20,
        y: element.y + 20,
      };
      setElements(prev => {
        setIsDirty(true);
        return [...prev, newElement];
      });
      setSelectedElementId(newElement.id);
    }
  };

  const handleElementPropertyChange = (elementId: string, property: string, value: any) => {
    setElements(prev => {
      setIsDirty(true);
      return prev.map(el => el.id === elementId ? { ...el, [property]: value } : el);
    });
  };

  // Shape tools
  const handleAddShape = (shapeType: 'rectangle' | 'circle' | 'arrow' | 'line') => {
    const shapeWidth = shapeType === 'circle' ? 100 : 120;
    const shapeHeight = shapeType === 'line' || shapeType === 'arrow' ? 0 : shapeType === 'circle' ? 100 : 80;
    
    const newElement: TemplateElement = {
      id: `shape-${Date.now()}`,
      type: 'shape',
      shapeType,
      x: 50 + Math.random() * (canvasDimensions.width - shapeWidth - 50),
      y: 50 + Math.random() * (canvasDimensions.height - Math.max(shapeHeight, 50) - 50),
      width: shapeWidth,
      height: shapeHeight,
      color: currentColor,
      strokeWidth: 3,
      layer: elements.length
    };
    setElements(prev => {
      setIsDirty(true);
      return [...prev, newElement];
    });
    setShowShapeMenu(false);
  };

  // Sticky notes
  const handleAddStickyNote = (color: string) => {
    const newElement: TemplateElement = {
      id: `sticky-${Date.now()}`,
      type: 'sticker',
      text: 'Click to edit',
      x: 50 + Math.random() * (canvasDimensions.width - 200),
      y: 50 + Math.random() * (canvasDimensions.height - 200),
      width: 150,
      height: 150,
      stickyColor: color,
      color: '#374151',
      fontSize: 16,
      fontFamily: 'Arial',
      layer: elements.length
    };
    setElements(prev => {
      setIsDirty(true);
      return [...prev, newElement];
    });
    setShowStickyNoteColors(false);
  };

  // Drawing tool
  const handleStartDrawing = () => {
    setIsDrawing(true);
    setCurrentPath([]);
  };

  const handleDrawing = (point: { x: number; y: number }) => {
    if (!isDrawing) return;
    setCurrentPath(prev => [...prev, point]);
  };

  const handleEndDrawing = () => {
    if (currentPath.length > 0) {
      const newElement: TemplateElement = {
        id: `drawing-${Date.now()}`,
        type: 'drawing',
        x: Math.min(...currentPath.map(p => p.x)),
        y: Math.min(...currentPath.map(p => p.y)),
        width: Math.max(...currentPath.map(p => p.x)) - Math.min(...currentPath.map(p => p.x)),
        height: Math.max(...currentPath.map(p => p.y)) - Math.min(...currentPath.map(p => p.y)),
        points: currentPath,
        color: currentColor,
        strokeWidth: 3,
        layer: elements.length
      };
      setElements(prev => {
        setIsDirty(true);
        return [...prev, newElement];
      });
    }
    setIsDrawing(false);
    setCurrentPath([]);
  };

  // Color palette extraction from images
  const extractColorsFromImage = (imageSrc: string) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      
      const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
      const colors = new Set<string>();
      
      // Sample colors from the image
      for (let i = 0; i < imageData!.data.length; i += 4 * 50) {
        const r = imageData!.data[i];
        const g = imageData!.data[i + 1];
        const b = imageData!.data[i + 2];
        const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        colors.add(hex);
        
        if (colors.size >= 5) break;
      }
      
      setSelectedColors(Array.from(colors).slice(0, 5));
    };
    
    img.src = imageSrc;
  };

  // Enhanced text with rich formatting
  const handleAddRichText = (formatting: { bold?: boolean; italic?: boolean; underline?: boolean; align?: string }) => {
    const newElement: TemplateElement = {
      id: `richtext-${Date.now()}`,
      type: 'text',
      text: 'Rich Text',
      x: 150,
      y: 150,
      color: currentColor,
      fontSize: 24,
      fontFamily: 'Arial',
      bold: formatting.bold || false,
      italic: formatting.italic || false,
      underline: formatting.underline || false,
      align: (formatting.align as 'left' | 'center' | 'right') || 'left',
      layer: elements.length
    };
    setElements(prev => {
      setIsDirty(true);
      return [...prev, newElement];
    });
  };

  // Background patterns
  const handleSetBackground = (pattern: 'none' | 'dots' | 'grid' | 'lines') => {
    setBackgroundPattern(pattern);
    
    // Add background element to canvas
    const newElement: TemplateElement = {
      id: `bg-${Date.now()}`,
      type: 'background',
      x: 0,
      y: 0,
      width: 800,
      height: 600,
      pattern,
      color: '#f3f4f6',
      layer: -1 // Always at the back
    };
    
    // Remove existing background
    setElements(prev => {
      const withoutBg = prev.filter(el => el.type !== 'background');
      setIsDirty(true);
      return pattern === 'none' ? withoutBg : [newElement, ...withoutBg];
    });
  };

  const handleSave = async () => {
    console.log('Save initiated...', { 
      currentUser: currentUser?.uid, 
      templateId, 
      isDocument, 
      isNewBlank,
      editName: editName,
      template: template?.name,
      hasTemplate: !!template,
      elementsCount: elements.length
    });
    
    if (!currentUser?.uid) {
      alert('Not authenticated');
      return;
    }

    // Ensure we have a valid name - don't default to "Untitled Design" if we have a template
    const designName = editName || template?.name || 'Untitled Design';
    console.log('Using design name:', designName);
    
    console.log('Save path decision:', {
      isNewBlank,
      templateId,
      isDocument,
      hasTemplate: !!template,
      condition1: isNewBlank || templateId === 'new',
      condition2: isDocument && templateId,
      condition3: !isDocument && template
    });

    try {
      // Generate thumbnail from canvas
      let previewUrl = editImageUrl || 'https://via.placeholder.com/400x300/f0f0f0/666?text=Mood+Board';
      
      if (stageRef && elements.length > 0) {
        try {
          console.log('Attempting to capture canvas thumbnail...');
          
          // Temporarily hide grid for thumbnail capture
          setHideGridForExport(true);
          
          // Wait a moment for the grid to hide and force a redraw
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Force stage to redraw
          stageRef.draw();
          
          // Capture canvas as thumbnail with better settings
          const dataURL = stageRef.toDataURL({
            pixelRatio: 1, // Higher quality for thumbnail
            mimeType: 'image/png', // PNG for better quality
            quality: 0.9,
            width: 400, // Fixed thumbnail width
            height: 300, // Fixed thumbnail height
          });
          
          // Validate the captured image
          if (dataURL && dataURL.length > 100 && dataURL.startsWith('data:image/')) {
            previewUrl = dataURL;
            console.log('Successfully captured canvas thumbnail');
          } else {
            console.warn('Invalid thumbnail captured, using placeholder');
          }
          
          // Restore grid
          setHideGridForExport(false);
        } catch (captureError) {
          console.error('Failed to capture thumbnail:', captureError);
          setHideGridForExport(false); // Ensure grid is restored on error
          // Keep the placeholder if capture fails
        }
      } else {
        console.log('No stage reference or elements available for thumbnail capture');
      }

      // If we still don't have a valid preview and have elements, create a simple fallback
      if (previewUrl === editImageUrl && elements.length > 0) {
        try {
          console.log('Creating fallback thumbnail...');
          const canvas = document.createElement('canvas');
          canvas.width = 400;
          canvas.height = 300;
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            // Create a gradient background
            const gradient = ctx.createLinearGradient(0, 0, 400, 300);
            gradient.addColorStop(0, '#f3f4f6');
            gradient.addColorStop(1, '#e5e7eb');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 400, 300);
            
            // Add design name
            ctx.fillStyle = '#374151';
            ctx.font = 'bold 24px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(designName, 200, 150);
            
            // Add element count
            ctx.fillStyle = '#6b7280';
            ctx.font = '16px Inter, sans-serif';
            ctx.fillText(`${elements.length} elements`, 200, 180);
            
            previewUrl = canvas.toDataURL('image/png');
            console.log('Created fallback thumbnail');
          }
        } catch (fallbackError) {
          console.error('Failed to create fallback thumbnail:', fallbackError);
        }
      }

      // For new blank designs, create a new document in Realtime Database
      if (isNewBlank || templateId === 'new') {
        // Create a fake template structure for forking
        const fakeTemplate: MoodBoardTemplate = {
          id: 'new',
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
          created_by: currentUser.displayName || 'Unknown',
          timestamp: Date.now(),
        };
        
        const newDocId = await forkTemplateToDocument(fakeTemplate, currentUser.uid);
        await updateDocument(newDocId, {
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
        });
        await setElementsArray(newDocId, elements);
        
        console.log('Saved new design with name:', designName, 'preview:', previewUrl.substring(0, 50) + '...');
        alert('Design saved successfully!');
        setIsDirty(false);
        setIsDocument(true);
        
        // Navigate to dashboard instead of the new document
        console.log('Navigating to dashboard after new design save');
        navigate('/dashboard');
        return;
      }
      
      // For existing documents, update them
      if (isDocument && templateId) {
        await updateDocument(templateId, {
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
        });
        await setElementsArray(templateId, elements);
        
        console.log('Updated existing design with name:', designName, 'preview:', previewUrl.substring(0, 50) + '...');
        alert('Design updated successfully!');
        setIsDirty(false);
        return;
      }
      
      // For template-based designs (first save), fork the template
      if (!isDocument && template) {
        console.log('Forking template:', template.name, 'for user:', currentUser.uid);
        
        const newDocId = await forkTemplateToDocument(template, currentUser.uid);
        console.log('Document created with ID:', newDocId);
        
        // Update with the user's custom name and elements
        await updateDocument(newDocId, {
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
        });
        console.log('Document updated with custom name:', designName);
        
        await setElementsArray(newDocId, elements);
        console.log('Elements array set for document:', newDocId);
        
        setIsDocument(true);
        console.log('Forked template and saved with name:', designName, 'preview:', previewUrl.substring(0, 50) + '...');
        alert('Design saved successfully!');
        setIsDirty(false);
        
        // Add a small delay to ensure the document is properly saved before navigation
        console.log('Waiting 500ms before navigation to ensure document is saved...');
        setTimeout(() => {
          console.log('Navigating to dashboard after successful save');
          navigate('/dashboard');
        }, 500);
        return;
      }
      
      alert('Unable to save - missing data');
    } catch (err: any) {
      console.error('Detailed save error:', err);
      alert(`Error saving design: ${err.message || 'Unknown error'}`);
    }
  };

  // Save function that doesn't navigate automatically (for unsaved changes hook)
  const handleSaveOnly = async () => {
    console.log('SaveOnly initiated...', { 
      currentUser: currentUser?.uid, 
      templateId, 
      isDocument, 
      isNewBlank,
      editName: editName,
      template: template?.name,
      hasTemplate: !!template,
      elementsCount: elements.length
    });
    
    if (!currentUser?.uid) {
      throw new Error('Not authenticated');
    }

    // Ensure we have a valid name - don't default to "Untitled Design" if we have a template
    const designName = editName || template?.name || 'Untitled Design';
    console.log('Using design name:', designName);
    
    try {
      // Generate thumbnail from canvas
      let previewUrl = editImageUrl || 'https://via.placeholder.com/400x300/f0f0f0/666?text=Mood+Board';
      
      if (stageRef && elements.length > 0) {
        try {
          console.log('Attempting to capture canvas thumbnail...');
          
          // Temporarily hide grid for thumbnail capture
          setHideGridForExport(true);
          
          // Wait a moment for the grid to hide and force a redraw
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Force stage to redraw
          stageRef.draw();
          
          // Capture canvas as thumbnail with better settings
          const dataURL = stageRef.toDataURL({
            pixelRatio: 1, // Higher quality for thumbnail
            mimeType: 'image/png', // PNG for better quality
            quality: 0.9,
            width: 400, // Fixed thumbnail width
            height: 300, // Fixed thumbnail height
          });
          
          // Validate the captured image
          if (dataURL && dataURL.length > 100 && dataURL.startsWith('data:image/')) {
            previewUrl = dataURL;
            console.log('Successfully captured canvas thumbnail');
          } else {
            console.warn('Invalid thumbnail captured, using placeholder');
          }
          
          // Restore grid
          setHideGridForExport(false);
        } catch (captureError) {
          console.error('Failed to capture thumbnail:', captureError);
          setHideGridForExport(false); // Ensure grid is restored on error
          // Keep the placeholder if capture fails
        }
      } else {
        console.log('No stage reference or elements available for thumbnail capture');
      }

      // If we still don't have a valid preview and have elements, create a simple fallback
      if (previewUrl === editImageUrl && elements.length > 0) {
        try {
          console.log('Creating fallback thumbnail...');
          const canvas = document.createElement('canvas');
          canvas.width = 400;
          canvas.height = 300;
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            // Create a gradient background
            const gradient = ctx.createLinearGradient(0, 0, 400, 300);
            gradient.addColorStop(0, '#f3f4f6');
            gradient.addColorStop(1, '#e5e7eb');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 400, 300);
            
            // Add design name
            ctx.fillStyle = '#374151';
            ctx.font = 'bold 24px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(designName, 200, 150);
            
            // Add element count
            ctx.fillStyle = '#6b7280';
            ctx.font = '16px Inter, sans-serif';
            ctx.fillText(`${elements.length} elements`, 200, 180);
            
            previewUrl = canvas.toDataURL('image/png');
            console.log('Created fallback thumbnail');
          }
        } catch (fallbackError) {
          console.error('Failed to create fallback thumbnail:', fallbackError);
        }
      }

      // For new blank designs, create a new document in Realtime Database
      if (isNewBlank || templateId === 'new') {
        // Create a fake template structure for forking
        const fakeTemplate: MoodBoardTemplate = {
          id: 'new',
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
          created_by: currentUser.displayName || 'Unknown',
          timestamp: Date.now(),
        };
        
        const newDocId = await forkTemplateToDocument(fakeTemplate, currentUser.uid);
        await updateDocument(newDocId, {
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
        });
        await setElementsArray(newDocId, elements);
        
        console.log('Saved new design with name:', designName, 'preview:', previewUrl.substring(0, 50) + '...');
        setIsDirty(false);
        setIsDocument(true);
        
        // DON'T navigate automatically - let the unsaved changes hook handle navigation
        console.log('SaveOnly: Design saved successfully without navigation');
        return;
      }
      
      // For existing documents, update them
      if (isDocument && templateId) {
        await updateDocument(templateId, {
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
        });
        await setElementsArray(templateId, elements);
        
        console.log('Updated existing design with name:', designName, 'preview:', previewUrl.substring(0, 50) + '...');
        setIsDirty(false);
        return;
      }
      
      // For template-based designs (first save), fork the template
      if (!isDocument && template) {
        console.log('Forking template:', template.name, 'for user:', currentUser.uid);
        
        const newDocId = await forkTemplateToDocument(template, currentUser.uid);
        console.log('Document created with ID:', newDocId);
        
        // Update with the user's custom name and elements
        await updateDocument(newDocId, {
          name: designName,
          preview_image_url: previewUrl,
          elements: elements,
        });
        console.log('Document updated with custom name:', designName);
        
        await setElementsArray(newDocId, elements);
        console.log('Elements array set for document:', newDocId);
        
        setIsDocument(true);
        console.log('Forked template and saved with name:', designName, 'preview:', previewUrl.substring(0, 50) + '...');
        setIsDirty(false);
        
        // DON'T navigate automatically - let the unsaved changes hook handle navigation
        console.log('SaveOnly: Template forked and saved successfully without navigation');
        return;
      }
      
      throw new Error('Unable to save - missing data');
    } catch (err: any) {
      console.error('Detailed save error:', err);
      throw err; // Re-throw for the hook to handle
    }
  };

  // Discard function that restores the original state
  const handleDiscardChanges = async () => {
    console.log('Discarding changes and restoring original state...', { 
      hasRevertState: !!revertState,
      isDocument,
      templateId 
    });
    
    if (!revertState) {
      console.log('No revert state available');
      return;
    }

    try {
      // Clear any pending auto-save
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        console.log('Cleared pending auto-save timeout');
      }

      // Restore UI state
      setEditName(revertState.name);
      setEditImageUrl(revertState.preview_image_url);
      setElements(revertState.elements || []);
      setIsDirty(false);
      console.log('Restored UI state to original values');

      // If this is a document, also restore the Firebase state
      if (isDocument && templateId) {
        console.log('Restoring Firebase document state...');
        
        // Restore document metadata
        await updateDocument(templateId, {
          name: revertState.name,
          preview_image_url: revertState.preview_image_url,
          elements: revertState.elements || []
        });
        
        // Restore elements array
        await setElementsArray(templateId, revertState.elements || []);
        
        console.log('Successfully restored Firebase document state');
      }
      
      console.log('Discard completed successfully');
    } catch (error) {
      console.error('Failed to discard changes:', error);
      throw error; // Re-throw for the hook to handle
    }
  };

  // Handle browser back button navigation - only set up once
  useEffect(() => {
    // Push initial state to detect back navigation
    window.history.pushState({ page: 'editor' }, '', window.location.href);
    
    const handlePopState = async (e: PopStateEvent) => {
      // Check isDirty at the time of the event, not from closure
      const currentIsDirty = isDirtyRef.current;
      
      if (currentIsDirty) {
        // Prevent the navigation by pushing the state back
        window.history.pushState({ page: 'editor' }, '', window.location.href);
        
        // Use the custom modal and wait for the result
        const canNavigate = await handleNavigationRequest('/dashboard');
        if (canNavigate) {
          navigate('/dashboard');
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleNavigationRequest, navigate]); // Add navigate to dependencies

  const handleDelete = async () => {
    if (!templateId) return;
    
    // For now, keep this as window.confirm since it's a different type of confirmation
    // This could be improved later with a custom delete modal
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        if (isDocument) {
          // Only owner can delete their document later; for now just navigate back
          alert('Deleting personal designs not implemented yet.');
        } else {
        await deleteTemplate(templateId);
        }
        navigate('/dashboard');
      } catch (err) {
        console.error('Error deleting template:', err);
        alert('Error deleting template');
      }
    }
  };

  const handleContainerClick = () => {
    setSelectedElementId(null);
  };

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);

  // Drag-to-pan handlers
  const handlePanStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (canvasContainerRef.current) {
      setIsPanning(true);
      canvasContainerRef.current.style.cursor = 'grabbing';
      panStart.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: canvasContainerRef.current.scrollLeft,
        scrollTop: canvasContainerRef.current.scrollTop,
      };
    }
  };
  const handlePanMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPanning || !canvasContainerRef.current || !panStart.current) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    canvasContainerRef.current.scrollLeft = panStart.current.scrollLeft - dx;
    canvasContainerRef.current.scrollTop = panStart.current.scrollTop - dy;
  };
  const handlePanEnd = () => {
    setIsPanning(false);
    if (canvasContainerRef.current) {
      canvasContainerRef.current.style.cursor = 'grab';
    }
    panStart.current = null;
  };

  const handleElementsChange = (els: TemplateElement[]) => {
    setElements(els);
    setIsDirty(true);
    // write to DB if isDocument
    if (isDocument && templateId) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setElementsArray(templateId, els);
      }, 200);
    }
  };

  // Add handlers for bring forward, send backward, lock
  const handleBringForward = (elementId: string) => {
    setElements(prev => {
      const idx = prev.findIndex(el => el.id === elementId);
      if (idx === -1 || idx === prev.length - 1) return prev;
      if (prev[idx].locked) return prev; // Don't move locked
      const newArr = [...prev];
      [newArr[idx], newArr[idx + 1]] = [newArr[idx + 1], newArr[idx]];
      setIsDirty(true);
      return newArr;
    });
  };

  // Move element one layer backward
  const handleSendBackward = (elementId: string) => {
    setElements(prev => {
      const idx = prev.findIndex(el => el.id === elementId);
      if (idx <= 0) return prev;
      if (prev[idx].locked) return prev; // Don't move locked
      const newArr = [...prev];
      [newArr[idx], newArr[idx - 1]] = [newArr[idx - 1], newArr[idx]];
      setIsDirty(true);
      return newArr;
    });
  };

  // Toggle lock on element
  const handleToggleLock = (elementId: string) => {
    setElements(prev => {
      setIsDirty(true);
      return prev.map(el => el.id === elementId ? { ...el, locked: !el.locked } : el);
    });
  };

  const setIsDirty = (value: boolean) => {
    setIsDirtyState(value);
    isDirtyRef.current = value;
  };

  const centerElements = (els: TemplateElement[], stageW: number, stageH: number): TemplateElement[] => {
    if (els.length === 0) return els;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    els.forEach(el => {
      const w = el.type === 'image' ? (el.width || 128) : 120;
      const h = el.type === 'image' ? (el.height || 128) : (el.fontSize || 24) * 1.5;
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + w);
      maxY = Math.max(maxY, el.y + h);
    });
    const contentW = maxX - minX;
    const contentH = maxY - minY;
    const offsetX = (stageW - contentW) / 2 - minX;
    const offsetY = (stageH - contentH) / 2 - minY;
    return els.map(el => ({ ...el, x: el.x + offsetX, y: el.y + offsetY }));
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-50">
      <AppHeader
        hasUnsavedChanges={isDirty}
        onNavigateAway={handleNavigationRequest}
        showSaveIndicator={isDirty}
      />

      {/* Quick Guide Floating Button */}
      <button
        onClick={() => setShowGuide(true)}
        className="fixed bottom-6 right-6 z-[70] px-3 py-2 rounded-full shadow-lg bg-white border hover:bg-gray-50 flex items-center gap-1 text-sm text-gray-700"
        title="How to use the editor"
      >
        <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600" />
        Guide
      </button>

      {/* Quick-start Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">How to use the editor</h3>
              </div>
              <button onClick={() => setShowGuide(false)} className="p-2 rounded hover:bg-gray-100">
                <XMarkIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4 border">
                <h4 className="font-medium text-gray-900 mb-2">1. Add items</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Use the left toolbar to add Text, Sticky notes, Shapes, or upload Images.</li>
                  <li>• Drag items onto the canvas and position them anywhere.</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <h4 className="font-medium text-gray-900 mb-2">2. Select and move</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Click any item to select it. Drag to move.</li>
                  <li>• Hold Shift to select multiple items.</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <h4 className="font-medium text-gray-900 mb-2">3. Resize and rotate</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Drag corner handles to resize.</li>
                  <li>• Use the rotate handle to rotate elements.</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <h4 className="font-medium text-gray-900 mb-2">4. Arrange</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Right-click to bring items forward/backward.</li>
                  <li>• Use alignment guides to snap items neatly.</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border md:col-span-2">
                <h4 className="font-medium text-gray-900 mb-2">5. Save and download</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Click Save to store changes.</li>
                  <li>• Download exports the canvas as PNG/PDF. Pro required for downloads.</li>
                </ul>
                <div className="mt-3 text-xs text-gray-500">Tip: Press Cmd/Ctrl + Z to undo, Cmd/Ctrl + Y to redo.</div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => setShowGuide(false)} className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                Got it
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for image uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageChange}
      />

      {/* Desktop Layout */}
      <div className="hidden lg:flex flex-1 h-full">
        {/* Left Sidebar: Toolset - More Compact */}
        <aside className="w-56 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-3 space-y-4 flex-1 overflow-y-auto">
            {/* Compact Tools - All in one section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tools</h3>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={handleAddImage} 
                  className="p-2 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 flex flex-col items-center gap-1 transition-colors"
                >
                  <PhotoIcon className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-blue-600">Upload</span>
                </button>
                <button 
                  onClick={() => setUnsplashOpen(true)}
                  className="p-2 bg-purple-50 hover:bg-purple-100 rounded border border-purple-200 flex flex-col items-center gap-1 transition-colors"
                >
                  <MagnifyingGlassIcon className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-purple-600">Unsplash</span>
                </button>
                <button 
                  onClick={handleAddText} 
                  className="p-2 bg-green-50 hover:bg-green-100 rounded border border-green-200 flex flex-col items-center gap-1 transition-colors"
                >
                  <DocumentIcon className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-green-600">Text</span>
                </button>
                <button 
                  onClick={() => setShowStickyNoteColors(true)}
                  className="p-2 bg-yellow-50 hover:bg-yellow-100 rounded border border-yellow-200 flex flex-col items-center gap-1 transition-colors"
                >
                  <FaceSmileIcon className="w-4 h-4 text-yellow-600" />
                  <span className="text-xs text-yellow-600">Sticky</span>
                </button>
                <button 
                  onClick={() => setShowShapeMenu(true)}
                  className="p-2 bg-indigo-50 hover:bg-indigo-100 rounded border border-indigo-200 flex flex-col items-center gap-1 transition-colors"
                >
                  <RectangleStackIcon className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs text-indigo-600">Shapes</span>
                </button>
                <button 
                  onClick={handleStartDrawing}
                  className={`p-2 rounded border flex flex-col items-center gap-1 transition-colors ${
                    isDrawing ? 'bg-red-100 border-red-300' : 'bg-red-50 hover:bg-red-100 border-red-200'
                  }`}
                >
                  <PaintBrushIcon className="w-4 h-4 text-red-600" />
                  <span className="text-xs text-red-600">{isDrawing ? 'Drawing' : 'Draw'}</span>
                </button>
                <button 
                  onClick={() => setShowGrid(!showGrid)}
                  className={`p-2 rounded border flex flex-col items-center gap-1 transition-colors ${
                    showGrid ? 'bg-gray-100 border-gray-300' : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                  }`}
                >
                  <Squares2X2Icon className="w-4 h-4 text-gray-600" />
                  <span className="text-xs text-gray-600">Grid</span>
                </button>
                <button 
                  onClick={() => setShowColorPicker(true)}
                  className="p-2 bg-orange-50 hover:bg-orange-100 rounded border border-orange-200 flex flex-col items-center gap-1 transition-colors"
                >
                  <SwatchIcon className="w-4 h-4 text-orange-600" />
                  <span className="text-xs text-orange-600">Colors</span>
                </button>
              </div>
            </div>

            {/* Layer Controls - Only when element selected */}
            {selectedElementId && (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Selected Element</h3>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => handleBringForward(selectedElementId)} 
                    className="p-2 bg-gray-50 hover:bg-gray-100 rounded border flex flex-col items-center gap-1"
                    title="Bring Forward"
                  >
                    <ChevronUpIcon className="w-4 h-4" />
                    <span className="text-xs">Forward</span>
                  </button>
                  <button 
                    onClick={() => handleSendBackward(selectedElementId)} 
                    className="p-2 bg-gray-50 hover:bg-gray-100 rounded border flex flex-col items-center gap-1"
                    title="Send Backward"
                  >
                    <ChevronDownIcon className="w-4 h-4" />
                    <span className="text-xs">Backward</span>
                  </button>
                  <button 
                    onClick={() => handleToggleLock(selectedElementId)} 
                    className="p-2 bg-yellow-50 hover:bg-yellow-100 rounded border border-yellow-200 flex flex-col items-center gap-1"
                    title="Lock/Unlock Element"
                  >
                    {elements.find(el => el.id === selectedElementId)?.locked ? 
                      <LockClosedIcon className="w-4 h-4 text-yellow-600" /> : 
                      <LockOpenIcon className="w-4 h-4 text-yellow-600" />
                    }
                    <span className="text-xs text-yellow-600">
                      {elements.find(el => el.id === selectedElementId)?.locked ? 'Unlock' : 'Lock'}
                    </span>
                  </button>
                  <button 
                    onClick={() => handleDeleteElement(selectedElementId)} 
                    className="p-2 bg-red-50 hover:bg-red-100 rounded border border-red-200 flex flex-col items-center gap-1"
                    title="Delete Element"
                  >
                    <TrashIcon className="w-4 h-4 text-red-600" />
                    <span className="text-xs text-red-600">Delete</span>
                  </button>
                </div>
              </div>
            )}

            {/* Templates Section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
                Templates
              </h3>
              <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                {availableTemplates.slice(0, 6).map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="p-2 bg-white hover:bg-gray-50 rounded border border-gray-200 flex items-center gap-2 text-left transition-colors"
                  >
                    <div className="w-12 h-8 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                      {template.preview_image_url ? (
                        <img 
                          src={template.preview_image_url} 
                          alt={template.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                          <span className="text-xs font-semibold text-blue-600">
                            {template.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">{template.name}</div>
                      <div className="text-xs text-gray-500">{template.elements?.length || 0} elements</div>
                    </div>
                  </button>
                ))}
                {availableTemplates.length === 0 && (
                  <div className="text-xs text-gray-500 text-center py-4">
                    No templates available
                  </div>
                )}
                {availableTemplates.length > 6 && (
                  <button
                    onClick={() => navigate('/templates')}
                    className="w-full mt-2 p-2 text-xs text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 rounded transition-colors"
                  >
                    View All Templates ({availableTemplates.length})
                  </button>
                )}
              </div>
            </div>

            {/* Save/Export - Always visible */}
            <div className="mt-auto pt-4 border-t border-gray-200">
              <div className="space-y-2">
                <button
                  onClick={handleSave}
                  disabled={!isDirty && !isDocument}
                  className="w-full p-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 4h16v16H4z"/>
                    <path d="M8 4v4h8V4"/>
                  </svg>
                  Save
                </button>
                <button 
                  onClick={handleDownload} 
                  className="w-full p-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 4v12m0 0l-4-4m4 4l4-4"/>
                  </svg>
                  Download
                </button>
              </div>
            </div>


          </div>
        </aside>

        {/* Main Canvas Area - Full Height */}
        <main className="flex-1 flex flex-col h-full">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error && !isNewBlank ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-red-500 text-center">{error}</div>
            </div>
          ) : template && (
            <div className="flex-1 flex flex-col h-full">
              {/* Compact Title Bar */}
              <div className="px-4 py-2 border-b bg-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isEditingTitle ? (
                    <input
                      className="text-lg font-semibold border-b border-blue-400 outline-none bg-transparent"
                      value={editName}
                      autoFocus
                      onChange={e => {
                        setEditName(e.target.value);
                        setIsDirty(true);
                      }}
                      onBlur={() => {
                        setIsEditingTitle(false);
                        if (!editName.trim()) {
                          setEditName(template?.name || 'Untitled Design');
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') setIsEditingTitle(false);
                      }}
                    />
                  ) : (
                    <h2
                      className="text-lg font-semibold cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => setIsEditingTitle(true)}
                    >
                      {editName} {isDirty && <span className="text-orange-500">*</span>}
                    </h2>
                  )}
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-500">by {template.created_by}</span>
                </div>
                
                {/* Zoom Controls - Compact */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCanvasScale(s => Math.max(0.2, +(s - 0.1).toFixed(2)))}
                    className="w-7 h-7 rounded border bg-white hover:bg-gray-50 flex items-center justify-center text-sm"
                  >
                    –
                  </button>
                  <span className="min-w-12 text-center text-sm font-medium">
                    {Math.round(canvasScale * 100)}%
                  </span>
                  <button
                    onClick={() => setCanvasScale(s => Math.min(2.0, +(s + 0.1).toFixed(2)))}
                    className="w-7 h-7 rounded border bg-white hover:bg-gray-50 flex items-center justify-center text-sm"
                  >
                    +
                  </button>
                  <div className="text-xs text-gray-400 ml-2">
                    {elements.length} elements
                  </div>
                </div>
              </div>
              
                             {/* Canvas - Full Height */}
               <div className="flex-1 bg-gray-50 p-4">
                 <div
                   ref={canvasContainerRef}
                   className="relative border rounded-lg overflow-hidden bg-white shadow-lg flex items-center justify-center"
                   style={{
                     width: canvasDimensions.width * canvasScale,
                     height: canvasDimensions.height * canvasScale,
                     cursor: isPanning ? 'grabbing' : 'grab',
                     userSelect: isPanning ? 'none' : 'auto',
                   }}
                   onMouseDown={handlePanStart}
                   onMouseMove={handlePanMove}
                   onMouseUp={handlePanEnd}
                   onMouseLeave={handlePanEnd}
                 >
                   <KonvaCanvas
                     elements={elements}
                     onElementsChange={handleElementsChange}
                     selectedElementId={selectedElementId}
                     onElementSelect={setSelectedElementId}
                     canvasScale={canvasScale}
                     width={canvasDimensions.width}
                     height={canvasDimensions.height}
                     onStageRef={setStageRef}
                     hideGrid={hideGridForExport}
                     onTextEdit={handleEditTextElement}
                   />
                 </div>
               </div>
            </div>
          )}
        </main>


      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden flex-1 flex flex-col relative">
        {/* Mobile Canvas Area */}
        <main className="flex-1 relative overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error && !isNewBlank ? (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-red-500 text-center">{error}</div>
            </div>
          ) : template && (
            <div className="h-full flex flex-col">
              {/* Mobile Title Bar - Compact */}
              <div className="bg-white border-b px-3 py-2 flex items-center justify-between">
                <div className="flex-1 min-w-0 mr-2">
                  {isEditingTitle ? (
                    <input
                      className="text-base font-semibold border-b border-blue-400 outline-none w-full"
                      value={editName}
                      autoFocus
                      onChange={e => {
                        setEditName(e.target.value);
                        setIsDirty(true);
                      }}
                      onBlur={() => {
                        setIsEditingTitle(false);
                        if (!editName.trim()) {
                          setEditName(template?.name || 'Untitled Design');
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') setIsEditingTitle(false);
                      }}
                    />
                  ) : (
                    <h2
                      className="text-base font-semibold truncate cursor-pointer"
                      onClick={() => setIsEditingTitle(true)}
                    >
                      {editName} {isDirty && <span className="text-orange-500">*</span>}
                    </h2>
                  )}
                  <div className="text-xs text-gray-500 mt-0.5">
                    by {template.created_by} • {elements.length} elements
                  </div>
                </div>
                
                {/* Mobile Controls */}
                <div className="flex items-center gap-1">
                  {/* Zoom Controls */}
                  <button
                    onClick={() => setCanvasScale(s => Math.max(0.2, +(s - 0.1).toFixed(2)))}
                    className="w-8 h-8 rounded border bg-white hover:bg-gray-50 flex items-center justify-center text-sm"
                  >
                    –
                  </button>
                  <span className="text-xs px-2 font-medium min-w-12 text-center">
                    {Math.round(canvasScale * 100)}%
                  </span>
                  <button
                    onClick={() => setCanvasScale(s => Math.min(2.0, +(s + 0.1).toFixed(2)))}
                    className="w-8 h-8 rounded border bg-white hover:bg-gray-50 flex items-center justify-center text-sm"
                  >
                    +
                  </button>
                  
                  <div className="border-l mx-2 h-6"></div>
                  
                  {/* Tools Button */}
                  <button
                    onClick={() => setIsLeftSidebarOpen(!isLeftSidebarOpen)}
                    className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M4 6h16M4 12h16M4 18h16"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Full Screen Canvas - Responsive */}
              <div className="flex-1 bg-gray-50 p-2">
                <div
                  ref={canvasContainerRef}
                  className="relative border rounded-lg overflow-hidden bg-white shadow-lg flex items-center justify-center"
                  style={{
                    width: canvasDimensions.width * canvasScale,
                    height: canvasDimensions.height * canvasScale,
                    cursor: isPanning ? 'grabbing' : 'grab',
                    userSelect: isPanning ? 'none' : 'auto',
                  }}
                  onMouseDown={handlePanStart}
                  onMouseMove={handlePanMove}
                  onMouseUp={handlePanEnd}
                  onMouseLeave={handlePanEnd}
                  onTouchStart={(e) => {
                    const touch = e.touches[0];
                    handlePanStart({
                      clientX: touch.clientX,
                      clientY: touch.clientY,
                    } as any);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    handlePanMove({
                      clientX: touch.clientX,
                      clientY: touch.clientY,
                    } as any);
                  }}
                  onTouchEnd={handlePanEnd}
                >
                  <KonvaCanvas
                    elements={elements}
                    onElementsChange={handleElementsChange}
                    selectedElementId={selectedElementId}
                    onElementSelect={setSelectedElementId}
                    canvasScale={canvasScale}
                    width={canvasDimensions.width}
                    height={canvasDimensions.height}
                    onStageRef={setStageRef}
                    hideGrid={hideGridForExport}
                    onTextEdit={handleEditTextElement}
                  />
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Mobile Floating Action Buttons - Compact Quick Tools */}
        <div className="absolute bottom-4 right-4 flex flex-col gap-2">
          <button
            onClick={handleAddImage}
            className="w-11 h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center"
          >
            <PhotoIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleAddText}
            className="w-11 h-11 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg flex items-center justify-center"
          >
            <DocumentIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowStickyNoteColors(true)}
            className="w-11 h-11 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white shadow-lg flex items-center justify-center"
          >
            <FaceSmileIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty && !isDocument}
            className="w-11 h-11 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M4 4h16v16H4z"/>
              <path d="M8 4v4h8V4"/>
            </svg>
          </button>
        </div>

        {/* Mobile Left Sidebar (Tools) */}
        <div className={`fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${isLeftSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="h-full flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Tools & Elements</h3>
              <button
                onClick={() => setIsLeftSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Quick Tools Grid */}
              <div>
                <h4 className="font-medium mb-3">Add Elements</h4>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => {
                      handleAddImage();
                      setIsLeftSidebarOpen(false);
                    }}
                    className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 flex flex-col items-center gap-2"
                  >
                    <PhotoIcon className="w-5 h-5 text-blue-600" />
                    <span className="text-xs text-blue-600 font-medium">Upload</span>
                  </button>
                  <button 
                    onClick={() => {
                      setUnsplashOpen(true);
                      setIsLeftSidebarOpen(false);
                    }}
                    className="p-3 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 flex flex-col items-center gap-2"
                  >
                    <MagnifyingGlassIcon className="w-5 h-5 text-purple-600" />
                    <span className="text-xs text-purple-600 font-medium">Unsplash</span>
                  </button>
                  <button 
                    onClick={() => {
                      handleAddText();
                      setIsLeftSidebarOpen(false);
                    }}
                    className="p-3 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 flex flex-col items-center gap-2"
                  >
                    <DocumentIcon className="w-5 h-5 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">Text</span>
                  </button>
                  <button 
                    onClick={() => {
                      setShowStickyNoteColors(true);
                      setIsLeftSidebarOpen(false);
                    }}
                    className="p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 flex flex-col items-center gap-2"
                  >
                    <FaceSmileIcon className="w-5 h-5 text-yellow-600" />
                    <span className="text-xs text-yellow-600 font-medium">Sticky</span>
                  </button>
                  <button 
                    onClick={() => {
                      setShowShapeMenu(true);
                      setIsLeftSidebarOpen(false);
                    }}
                    className="p-3 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 flex flex-col items-center gap-2"
                  >
                    <RectangleStackIcon className="w-5 h-5 text-indigo-600" />
                    <span className="text-xs text-indigo-600 font-medium">Shapes</span>
                  </button>
                  <button 
                    onClick={() => {
                      handleStartDrawing();
                      setIsLeftSidebarOpen(false);
                    }}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-2 ${
                      isDrawing ? 'bg-red-100 border-red-300' : 'bg-red-50 hover:bg-red-100 border-red-200'
                    }`}
                  >
                    <PaintBrushIcon className="w-5 h-5 text-red-600" />
                    <span className="text-xs text-red-600 font-medium">{isDrawing ? 'Drawing' : 'Draw'}</span>
                  </button>
                  <button 
                    onClick={() => {
                      setShowGrid(!showGrid);
                      setIsLeftSidebarOpen(false);
                    }}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-2 ${
                      showGrid ? 'bg-gray-100 border-gray-300' : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                    }`}
                  >
                    <Squares2X2Icon className="w-5 h-5 text-gray-600" />
                    <span className="text-xs text-gray-600 font-medium">Grid</span>
                  </button>
                  <button 
                    onClick={() => {
                      setShowColorPicker(true);
                      setIsLeftSidebarOpen(false);
                    }}
                    className="p-3 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 flex flex-col items-center gap-2"
                  >
                    <SwatchIcon className="w-5 h-5 text-orange-600" />
                    <span className="text-xs text-orange-600 font-medium">Colors</span>
                  </button>
                </div>
              </div>

              {/* Layer Controls */}
              {selectedElementId && (
                <div>
                  <h4 className="font-medium mb-3">Selected Element</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={() => {
                        handleBringForward(selectedElementId);
                        setIsLeftSidebarOpen(false);
                      }}
                      className="p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border flex flex-col items-center gap-1"
                    >
                      <ChevronUpIcon className="w-5 h-5" />
                      <span className="text-xs">Forward</span>
                    </button>
                    <button 
                      onClick={() => {
                        handleSendBackward(selectedElementId);
                        setIsLeftSidebarOpen(false);
                      }}
                      className="p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border flex flex-col items-center gap-1"
                    >
                      <ChevronDownIcon className="w-5 h-5" />
                      <span className="text-xs">Backward</span>
                    </button>
                    <button 
                      onClick={() => {
                        handleToggleLock(selectedElementId);
                        setIsLeftSidebarOpen(false);
                      }}
                      className="p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 flex flex-col items-center gap-1"
                    >
                      {elements.find(el => el.id === selectedElementId)?.locked ? 
                        <LockClosedIcon className="w-5 h-5 text-yellow-600" /> : 
                        <LockOpenIcon className="w-5 h-5 text-yellow-600" />
                      }
                      <span className="text-xs text-yellow-600">
                        {elements.find(el => el.id === selectedElementId)?.locked ? 'Unlock' : 'Lock'}
                      </span>
                    </button>
                    <button 
                      onClick={() => {
                        handleDuplicateElement(selectedElementId);
                        setIsLeftSidebarOpen(false);
                      }}
                      className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 flex flex-col items-center gap-1"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <rect x="8" y="8" width="8" height="8" rx="2"/>
                        <rect x="4" y="4" width="8" height="8" rx="2"/>
                      </svg>
                      <span className="text-xs text-blue-600">Duplicate</span>
                    </button>
                    <button 
                      onClick={() => {
                        handleDeleteElement(selectedElementId);
                        setIsLeftSidebarOpen(false);
                      }}
                      className="p-3 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 flex flex-col items-center gap-1 col-span-2"
                    >
                      <TrashIcon className="w-5 h-5 text-red-600" />
                      <span className="text-xs text-red-600">Delete Element</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Templates */}
              <div>
                <h4 className="font-medium mb-3">Ready-Made Templates</h4>
                <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                  {availableTemplates.slice(0, 8).map((template) => (
                    <button
                      key={template.id}
                      onClick={() => {
                        handleTemplateSelect(template);
                        setIsLeftSidebarOpen(false);
                      }}
                      className="p-2 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 flex flex-col items-center gap-2 transition-colors"
                    >
                      <div className="w-full h-16 bg-gray-100 rounded overflow-hidden">
                        {template.preview_image_url ? (
                          <img 
                            src={template.preview_image_url} 
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                            <span className="text-sm font-semibold text-blue-600">
                              {template.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-center w-full">
                        <div className="text-xs font-medium text-gray-900 truncate">{template.name}</div>
                        <div className="text-xs text-gray-500">{template.elements?.length || 0} elements</div>
                      </div>
                    </button>
                  ))}
                  {availableTemplates.length === 0 && (
                    <div className="col-span-2 text-sm text-gray-500 text-center py-6">
                      No templates available
                    </div>
                  )}
                </div>
                {availableTemplates.length > 8 && (
                  <button
                    onClick={() => {
                      navigate('/templates');
                      setIsLeftSidebarOpen(false);
                    }}
                    className="w-full mt-3 p-2 text-sm text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                  >
                    View All Templates ({availableTemplates.length})
                  </button>
                )}

                {/* Save Actions */}
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => {
                      handleSave();
                      setIsLeftSidebarOpen(false);
                    }}
                    disabled={!isDirty && !isDocument}
                    className="w-full p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M4 4h16v16H4z"/>
                      <path d="M8 4v4h8V4"/>
                    </svg>
                    Save Design
                  </button>
                  <button 
                    onClick={() => {
                      handleDownload();
                      setIsLeftSidebarOpen(false);
                    }}
                    className="w-full p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 4v12m0 0l-4-4m4 4l4-4"/>
                    </svg>
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isLeftSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsLeftSidebarOpen(false)}
          />
        )}
      </div>

      <AppFooter />
      <UnsavedChangesModal />
      {showUpgrade && (
        <UpgradeToProModal isOpen={true} onClose={() => setShowUpgrade(false)} context="design" />
      )}

      {/* Unsplash Search Dialog */}
      {unsplashOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Dialog Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Search Unsplash Images</h2>
              <button
                onClick={() => setUnsplashOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={unsplashQuery}
                    onChange={(e) => setUnsplashQuery(e.target.value)}
                    placeholder="Search for images (e.g., nature, business, abstract)..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && unsplashQuery.trim()) {
                        handleUnsplashSearch();
                      }
                    }}
                  />
                  <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                <button
                  onClick={handleUnsplashSearch}
                  disabled={unsplashLoading || !unsplashQuery.trim()}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {unsplashLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="w-4 h-4" />
                      Search
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-6">
              {unsplashLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-500">Searching Unsplash...</p>
                  </div>
                </div>
              )}

              {!unsplashLoading && unsplashResults.length === 0 && unsplashQuery && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MagnifyingGlassIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg">No images found</p>
                  <p className="text-gray-400 text-sm mt-1">Try different search terms</p>
                </div>
              )}

              {!unsplashLoading && unsplashResults.length === 0 && !unsplashQuery && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg">Search for beautiful images</p>
                  <p className="text-gray-400 text-sm mt-1">Type keywords above to find the perfect image</p>
                </div>
              )}

              {unsplashResults.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {unsplashResults.map((img) => (
                    <div
                      key={img.id}
                      className="group relative bg-gray-100 rounded-lg overflow-hidden aspect-square cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
                      onClick={() => handleAddUnsplashImage(img.urls.regular)}
                    >
                      <img
                        src={img.urls.small}
                        alt={img.alt_description || 'Unsplash image'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="bg-white text-gray-900 px-3 py-2 rounded-lg font-medium flex items-center gap-2">
                            <PlusIcon className="w-4 h-4" />
                            Add Image
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2">
                        <div className="bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 truncate">
                          by {img.user?.name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-500">
                Images provided by{' '}
                <a
                  href="https://unsplash.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Unsplash
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Shape Menu */}
      {showShapeMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Shape</h3>
              <button
                onClick={() => setShowShapeMenu(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleAddShape('rectangle')}
                className="p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border flex flex-col items-center gap-2"
              >
                <RectangleStackIcon className="w-8 h-8 text-gray-600" />
                <span className="text-sm font-medium">Rectangle</span>
              </button>
              <button
                onClick={() => handleAddShape('circle')}
                className="p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border flex flex-col items-center gap-2"
              >
                <CircleStackIcon className="w-8 h-8 text-gray-600" />
                <span className="text-sm font-medium">Circle</span>
              </button>
              <button
                onClick={() => handleAddShape('arrow')}
                className="p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border flex flex-col items-center gap-2"
              >
                <ArrowRightIcon className="w-8 h-8 text-gray-600" />
                <span className="text-sm font-medium">Arrow</span>
              </button>
              <button
                onClick={() => handleAddShape('line')}
                className="p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border flex flex-col items-center gap-2"
              >
                <MinusIcon className="w-8 h-8 text-gray-600" />
                <span className="text-sm font-medium">Line</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Note Colors */}
      {showStickyNoteColors && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Choose Sticky Note Color</h3>
              <button
                onClick={() => setShowStickyNoteColors(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { color: '#fef08a', name: 'Yellow' },
                { color: '#fed7d7', name: 'Pink' },
                { color: '#c6f6d5', name: 'Green' },
                { color: '#bee3f8', name: 'Blue' },
                { color: '#e9d8fd', name: 'Purple' },
                { color: '#fed7aa', name: 'Orange' }
              ].map(({ color, name }) => (
                <button
                  key={color}
                  onClick={() => handleAddStickyNote(color)}
                  className="p-4 rounded-lg border-2 border-gray-200 hover:border-gray-400 transition-colors flex flex-col items-center gap-2"
                  style={{ backgroundColor: color }}
                >
                  <span className="text-sm font-medium text-gray-700">{name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Color Picker */}
      {showColorPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Color Palette</h3>
              <button
                onClick={() => setShowColorPicker(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Current Color</h4>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg border-2 border-gray-200"
                    style={{ backgroundColor: currentColor }}
                  />
                  <input
                    type="color"
                    value={currentColor}
                    onChange={(e) => setCurrentColor(e.target.value)}
                    className="w-20 h-10 rounded border"
                  />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2">Quick Colors</h4>
                <div className="grid grid-cols-5 gap-2">
                  {selectedColors.map((color, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentColor(color)}
                      className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-gray-400 transition-colors"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styled Text Edit Modal */}
      {showTextEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 font-['Poppins']">
                Edit Text
              </h3>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-['Inter']">
                  Text Content
                </label>
                <textarea
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none font-['Inter']"
                  rows={3}
                  placeholder="Enter your text here..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleTextEditSubmit();
                    }
                    if (e.key === 'Escape') {
                      handleTextEditCancel();
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleTextEditCancel}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium font-['Inter'] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTextEditSubmit}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg font-medium font-['Inter'] transition-all duration-200 shadow-md hover:shadow-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorPage; 